<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import { useNow } from '@vueuse/core';
  import {
    addDays,
    formatDateLong,
    toDateKey,
  } from '@/domain/reservations/reservation-time';
  const props = withDefaults(
    defineProps<{
      value: string;
      timeZone: string;
      minimumDate?: string;
      disabled?: boolean;
      error?: string;
    }>(),
    { disabled: false, error: undefined, minimumDate: undefined }
  );
  const emit = defineEmits<{ (event: 'input', value: string): void }>();
  const proxy = getCurrentInstance()!.proxy;
  const now = useNow({ interval: 60000 });
  const today = computed(() => toDateKey(now.value, props.timeZone));
  const days = computed(() =>
    Array.from({ length: 7 }, (_, index) => {
      const date = addDays(props.minimumDate ?? today.value, index);
      return {
        date,
        title: formatDateLong(date, props.timeZone, proxy.$i18n.locale),
        label:
          date === today.value || date === addDays(today.value, 1)
            ? String(
                proxy.$t(
                  date === today.value
                    ? 'reservations.dayStrip.today'
                    : 'reservations.dayStrip.tomorrow'
                )
              )
            : new Intl.DateTimeFormat(proxy.$i18n.locale, {
                weekday: 'short',
                timeZone: 'UTC',
              }).format(new Date(`${date}T12:00:00Z`)),
        day: Number(date.slice(-2)),
      };
    })
  );
  function setDate(event: Event) {
    if (event.target instanceof HTMLInputElement)
      emit('input', event.target.value);
  }
</script>
<template>
  <div class="reservation-day-strip">
    <div class="days">
      <button
        v-for="day in days"
        :key="day.date"
        type="button"
        class="day-choice"
        :class="{ selected: value === day.date }"
        :aria-label="day.title"
        :aria-pressed="value === day.date"
        :disabled="disabled"
        @click="emit('input', day.date)"
      >
        <span>{{ day.label }}</span
        ><strong>{{ day.day }}</strong>
      </button>
    </div>
    <label class="other-date"
      ><span>{{ $t('reservations.dayStrip.other') }}</span
      ><input
        type="date"
        :value="value"
        :min="minimumDate ?? today"
        :disabled="disabled"
        @input="setDate"
    /></label>
    <p v-if="error" class="error" role="alert">{{ error }}</p>
  </div>
</template>
<style scoped lang="scss">
  .reservation-day-strip {
    @apply tw-flex tw-flex-col tw-gap-3;
    .days {
      @apply tw-grid tw-grid-cols-7 tw-gap-1.5;
    }
    .day-choice {
      @apply tw-flex tw-min-h-14 tw-min-w-0 tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-1 tw-py-2 tw-text-xs tw-text-neutral-600;
      strong {
        @apply tw-text-sm tw-font-semibold tw-tabular-nums;
      }
      &.selected {
        @apply tw-border-neutral-900 tw-bg-neutral-900 tw-text-white;
      }
      &:disabled {
        @apply tw-opacity-50;
      }
    }
    .other-date {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-text-sm tw-text-neutral-600;
      input {
        @apply tw-min-h-11 tw-min-w-0 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-3 tw-text-sm tw-text-neutral-900;
      }
    }
    .error {
      @apply tw-m-0 tw-text-xs tw-text-red-600;
    }
  }
</style>
